/**
 * Money is integer cents everywhere in this app.
 *
 * Never a float. `0.1 + 0.2` is the classic demonstration, but the one that
 * matters here is that these numbers become the amount debited from a
 * customer's bank account — a cent of drift is a real discrepancy someone has
 * to reconcile, and an amount that doesn't match the invoice is a returned
 * entry and a phone call.
 *
 * Postgres holds them as `bigint`; JSON carries them as plain integers.
 */
/** Largest amount we will accept. $10,000,000 — far above any real invoice, low enough to catch a slipped decimal. */
export const MAX_AMOUNT_CENTS = 1_000_000_000;
export function isValidAmountCents(value) {
    return (typeof value === 'number' &&
        Number.isSafeInteger(value) &&
        value > 0 &&
        value <= MAX_AMOUNT_CENTS);
}
/**
 * Parses a user-typed dollar amount into cents.
 *
 * Accepts `1234.56`, `$1,234.56`, `1234`, and returns null for anything else —
 * including more than two decimal places, which is nearly always a typo rather
 * than a request for sub-cent precision.
 *
 * Internal whitespace is rejected rather than stripped. `1 234.56` is either a
 * slip of the space bar or the European thousands convention, and the two
 * readings differ by a factor of a thousand. Refusing it costs the admin one
 * retype; guessing wrong debits the wrong amount from a customer's account.
 */
export function parseAmountToCents(input) {
    const cleaned = input.trim().replace(/[$,]/g, '');
    if (cleaned === '')
        return null;
    if (!/^-?\d+(\.\d{1,2})?$/.test(cleaned))
        return null;
    const negative = cleaned.startsWith('-');
    const [whole = '0', fraction = ''] = cleaned.replace('-', '').split('.');
    const cents = Number(whole) * 100 + Number(fraction.padEnd(2, '0'));
    if (!Number.isSafeInteger(cents))
        return null;
    return negative ? -cents : cents;
}
/** `123456` -> `"1,234.56"`. No currency symbol; callers add it where it belongs. */
export function formatCents(cents) {
    const negative = cents < 0;
    const absolute = Math.abs(cents);
    const whole = Math.floor(absolute / 100);
    const fraction = String(absolute % 100).padStart(2, '0');
    return `${negative ? '-' : ''}${whole.toLocaleString('en-US')}.${fraction}`;
}
/** `123456` -> `"$1,234.56"`, for display. */
export function formatUsd(cents) {
    const negative = cents < 0;
    return `${negative ? '-' : ''}$${formatCents(Math.abs(cents))}`;
}
/**
 * Plain `1234.56` with no separators — the form the bank CSV wants.
 *
 * Deliberately not `formatCents`: a thousands separator inside an unquoted CSV
 * field would split it into two columns and silently corrupt the file.
 */
export function centsToPlainDecimal(cents) {
    const negative = cents < 0;
    const absolute = Math.abs(cents);
    return `${negative ? '-' : ''}${Math.floor(absolute / 100)}.${String(absolute % 100).padStart(2, '0')}`;
}
export function sumCents(values) {
    return values.reduce((total, value) => total + value, 0);
}
//# sourceMappingURL=money.js.map